'use client';

import { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface CourseCardProps {
  course: {
    id: string;
    title: string;
    description: string;
    duration: string;
    image: string;
    alt: string;
    icon: string;
    gradient: string;
    syllabus?: string[];
    languages?: string[];
  };
}

export default function CourseCard({ course }: CourseCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="group bg-card border border-border rounded-2xl overflow-hidden hover:border-primary/50 hover:shadow-2xl transition-all duration-300">
      {/* Image */}
      <div className="relative h-64 overflow-hidden">
        <AppImage
          src={course.image}
          alt={course.alt}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className={`absolute inset-0 bg-gradient-to-br ${course.gradient} opacity-20 group-hover:opacity-40 transition-opacity`}></div>
        
        {/* Icon Overlay */}
        <div className={`absolute top-4 right-4 w-14 h-14 rounded-xl bg-gradient-to-br ${course.gradient} flex items-center justify-center shadow-lg`}>
          <Icon name={course.icon as any} size={28} className="text-white" />
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="font-display text-2xl uppercase tracking-tight text-foreground mb-3">
          {course.title}
        </h3>
        <p className="text-base text-muted-foreground leading-relaxed mb-4">
          {course.description}
        </p>

        {/* Course Info */}
        <div className="flex items-center gap-6 mb-4 text-sm">
          <div className="flex items-center gap-2">
            <Icon name="ClockIcon" size={18} className="text-primary" />
            <span className="text-foreground font-medium">{course.duration}</span>
          </div>
        </div>

        {/* Languages List for Coding Course */}
        {course.languages && (
          <div className="bg-muted/20 rounded-xl p-4 mb-4">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-foreground mb-3">
              Available Languages
            </h4>
            <div className="flex flex-wrap gap-2">
              {course.languages.map((language, index) => (
                <span
                  key={`language_${course.id}_${index}`}
                  className="inline-flex items-center gap-1 bg-primary/10 text-primary px-3 py-1.5 rounded-full text-xs font-medium"
                >
                  <Icon name="CodeBracketIcon" size={14} />
                  {language}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Syllabus Toggle - Hidden for Coding Course */}
        {course.syllabus && (
          <>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary/80 transition-colors mb-4"
            >
              {isExpanded ? 'Hide' : 'View'} Syllabus
              <Icon
                name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                size={16}
                className="transition-transform"
              />
            </button>

            {/* Syllabus List */}
            {isExpanded && (
              <div className="bg-muted/20 rounded-xl p-4 mb-4 animate-fadeIn">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-foreground mb-3">
                  Course Highlights
                </h4>
                <ul className="space-y-2">
                  {course.syllabus.map((item, index) => (
                    <li key={`syllabus_${course.id}_${index}`} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Icon name="CheckCircleIcon" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}

        {/* CTA Button */}
        <Link
          href="/contact"
          className="block w-full bg-primary text-primary-foreground text-center px-6 py-3 rounded-full text-sm font-semibold uppercase tracking-wider hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl"
        >
          Enroll Now
        </Link>
      </div>
    </div>
  );
}